/**
 * Where a notification takes you when clicked.
 *
 * A lib rather than a function inside NotificationBell, for the same reason
 * `analyticsRedirect.ts` is one: it is a decision about where a thing leads,
 * it has no React in it, and the component that renders it drags in the
 * router, which does not resolve under jest.
 *
 * The rule it exists to keep: a notification the backend can send and this
 * file does not name is a dead click. The panel opens, the row is clickable,
 * and nothing happens. That is how the three team notifications shipped on
 * 19 Oct, so the mapping is pinned by its own tests.
 */

/** Only what the routing reads. */
export type RoutableNotification = {
  type: string;
  scenarioId?: string | null;
  payload?: Record<string, any> | null;
};

export function destinationFor(n: RoutableNotification): string | null {
  const p = n.payload || {};
  switch (n.type) {
    case 'assignment_created':
      return '/practice';
    case 'scenario_force_deleted':
      return '/practice'; // Recently Deleted modal is accessible from there
    // The outcome and the reviewer's note render on the author's own card
    // under My scenarios, so all four land in the same place.
    case 'scenario_reshared':
    case 'scenario_promoted':
    case 'submission_changes_requested':
    case 'submission_declined':
      return n.scenarioId ? '/practice' : null;
    // The four recurring team notifications. Each names something the
    // manager is meant to act on, so each opens the page that acts on it:
    // the roster for a person who has gone quiet, Overview for the week, and
    // the learner's own practice list for a deadline.
    case 'team_attention_alert':
      return p.userId ? `/team/members?memberId=${p.userId}&autoExpand=1` : '/team/members';
    // An improvement highlight leads to the same place as an alert, because
    // the thing to do with it is the same: open that person and have the
    // conversation. The roster panel is where their assigned practice and
    // coaching summary already are.
    case 'team_improvement_highlight':
      return p.userId ? `/team/members?memberId=${p.userId}&autoExpand=1` : '/team/members';
    case 'team_weekly_summary':
      return '/team/overview';
    case 'assignment_reminder':
      return '/practice';
    /* Account deletion. The owner's notification opens the screen that
     * approves it, and the member's opens the screen they asked from, which
     * is also where they can ask again. Both are Settings deep links, so the
     * tab and section have to match the ids in pages/Settings.tsx: a wrong
     * section silently lands on the tab's first one. */
    case 'account_deletion_requested':
      return '/settings?tab=people&section=roles';
    case 'account_deletion_declined':
      return '/settings?tab=account&section=delete';
    default:
      return null;
  }
}

/** Every type the recurring jobs in `utils/teamDigestScheduler.js` can send. */
export const TEAM_NOTIFICATION_TYPES = [
  'team_weekly_summary',
  'team_attention_alert',
  'team_improvement_highlight',
  'assignment_reminder',
] as const;

/**
 * The deletion notifications, which come from routes/users.js rather than a
 * recurring job. Listed so the routing test can cover them the same way it
 * covers the team ones: a type the backend can send and this file does not
 * name is a dead click.
 */
export const ACCOUNT_NOTIFICATION_TYPES = [
  'account_deletion_requested',
  'account_deletion_declined',
] as const;
